import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  hideHeaderOverlayScrim,
  isHeaderOverlayScrimTarget,
  showHeaderOverlayScrim,
  updateHeaderOverlayTop,
} from '@theme/header-overlay-scrim';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('click', this.#handleDocumentClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;

    const target = event.target;
    if (!(target instanceof Element) || !isHeaderOverlayScrimTarget(target)) return;

    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Monotonic token identifying the most recent open/close request. A deferred
   * close `reset()` only runs if its token still matches, so a reopen that
   * happens mid-animation cancels the stale reset instead of being clobbered.
   * @type {number}
   */
  #stateGeneration = 0;

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event?.type === 'click') event.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#stateGeneration++;

    summary.setAttribute('aria-expanded', 'true');
    details.open = true;

    this.preventInitialAccordionAnimations(details);
    this.#positionMaisonDrawer(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      showHeaderOverlayScrim();

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const generation = ++this.#stateGeneration;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    hideHeaderOverlayScrim();
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (this.#maisonReposition) {
      window.removeEventListener('resize', this.#maisonReposition);
      window.removeEventListener('scroll', this.#maisonReposition);
      this.#maisonReposition = null;
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    requestAnimationFrame(() => {
      onAnimationEnd(
        drawer || details,
        () => {
          if (generation !== this.#stateGeneration) return;
          reset(details);
          if (details === this.refs.details) {
            removeTrapFocus();
            const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
            openDetails.forEach(reset);
          } else {
            trapFocus(this.refs.details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Pin the mobile drawer to the bottom edge of the header.
   * @param {HTMLDetailsElement} details
   */
  #positionMaisonDrawer(details) {
    const maison = details.querySelector('.menu-drawer--maison');
    if (!maison) return;

    const header = this.closest('#header-component');
    if (!header) return;

    const update = () => {
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      details.style.setProperty('--maison-drawer-top', `${bottom}px`);
      updateHeaderOverlayTop();
    };

    update();

    this.#maisonReposition = update;
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
  }

  /** @type {(() => void) | null} */
  #maisonReposition = null;

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
